import React, { useState, useEffect, FC, ReactElement, ReactNode } from "react";
import { data_fetch_api_resource } from "../../utils/http_functions";
import { MenuItem, Select, FormControl, InputLabel, FormHelperText, SelectChangeEvent } from "@mui/material";
import { useSettingsContext } from "../Settings/hooks/useSettingsContext";
import { useTranslation } from "react-i18next";

interface CategoryItem {
  id: number;
  name: string;
}

interface MetaParams {
  touched?: boolean;
  error?: string;
}

interface CategorySelectFieldParams {
  onChange: (newValue: number) => void;
  value?: number;
  label?: string;
  readOnly?: boolean;
  onAvailableItemsUpdate?: () => void;
  onSelectUpdate?: (value: number, label: string) => void;
  meta?: MetaParams;
  autolectures: boolean;
  unsigned?: boolean;
}

const CategorySelectField: FC<CategorySelectFieldParams> = ({
  onChange,
  value,
  label,
  readOnly,
  onAvailableItemsUpdate,
  onSelectUpdate,
  meta,
  autolectures,
  unsigned=false,
  ...rest
}) => {

  const Settings = useSettingsContext();
  const { t } = useTranslation();

  const [availableItems, setAvailableItems] = useState<CategoryItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<number | null>(null);

  const fetchAvailableItems = async () => {
    let endpoint = "crm/cases/categories/";
    if (unsigned) {
      endpoint = "crm/cases/categories/?unsigned=true";
    }
    const response = await data_fetch_api_resource(null, endpoint);
    if (response.status === 200) {
      const { items } = response.data;

      setAvailableItems(items);

      if (items && typeof onAvailableItemsUpdate === "function") {
        onAvailableItemsUpdate();
      }

      let defaultSelectedItem = items?.[0]?.id ?? null;
      if (value) {
        defaultSelectedItem = value;
      }
      setSelectedItem(defaultSelectedItem);
      onChange(defaultSelectedItem);
    }
  };

  const handleChange = (
    event: SelectChangeEvent<unknown>,
    child: ReactNode
  ) => {
    const value = Number(event.target.value);

    setSelectedItem(value);
    onChange(value);
    if (typeof onSelectUpdate === "function" && React.isValidElement(child)) {
      onSelectUpdate(value, child.props.children);
    }
  };

  const setAutolectures = () => {
    let autoreads = Settings?.features?.contactAutoReads ?? false;
    if (autoreads){
      if (autolectures) {
        const categ = availableItems.filter((ct) => ct.id === 20)[0]; //20 is the code for autolectures @lumina
        setSelectedItem(categ.id)
        onChange(categ.id)
      }
    }
  }

  useEffect(() => {
    fetchAvailableItems();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setAutolectures();
    // eslint-disable-next-line
  }, [autolectures, availableItems]);

  return (
    availableItems.length !== 1 ?
      <div>
        <FormControl fullWidth={true}
          disabled={readOnly}
        >
          <InputLabel shrink htmlFor="">
            {(label || t('common:text.contact_category')) + "*"}
          </InputLabel>
          <Select<number>
            value={selectedItem ?? ''}
            onChange={handleChange}
            autoWidth={false}
            {...rest}
          >
            {(availableItems?.length > 0 &&
              availableItems.map((c) => {
                return <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>;
              })) ||
              []}
          </Select>
        </FormControl>
        {!!(meta?.touched) &&
          <FormHelperText error={true}>
            {meta?.error}
          </FormHelperText>
        }
      </div>
      : 
      <div></div>
  );
};

export default CategorySelectField;
